/*
 * WCAG 2.2.2 Pause, Stop, Hide (audit 2026-09-23).
 * Autoplaying, looping videos without native controls (hero/banner b-roll, Videowise
 * background videos) get a visible Pause/Play button. Visitors who prefer reduced motion
 * get them paused on load. Videos with native controls already have a pause control.
 *
 * Performance (2026-09-28): the page is watched for new videos, but the DOM check runs at most
 * once per 100 ms and never measures layout. It used to run on every DOM change and call
 * getBoundingClientRect on each small video, which forced a full layout for every change the
 * third-party scripts made while the page loaded (about 250 to 300 ms of blocking time on the
 * PDP in Lighthouse). Videos that are too small for a button when found are now measured by a
 * ResizeObserver, which reports sizes after the browser's own layout.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MIN_WIDTH = 120;
  const eligible = (video) => !(video.dataset.a11yPause || video.controls || !video.autoplay || !video.loop);

  const addButton = (video) => {
    video.dataset.a11yPause = '1';
    const host = video.parentElement;
    if (!host) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'a11y-video-pause';
    const sync = () => {
      const paused = video.paused;
      btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      btn.setAttribute('aria-pressed', String(paused));
      btn.innerHTML = paused
        ? '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><path d="M3 1.5v9l7.5-4.5z" fill="currentColor"/></svg>'
        : '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><rect x="2" y="1.5" width="2.5" height="9" fill="currentColor"/><rect x="7.5" y="1.5" width="2.5" height="9" fill="currentColor"/></svg>';
    };
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    if (reduce.matches) video.pause();
    sync();
    host.appendChild(btn);
  };

  // Videos waiting to be big enough for a button. Sizes are read inside the ResizeObserver
  // callback, where layout is already up to date, and all reads happen before any button is added.
  const watched = new WeakSet();
  const sizes = new ResizeObserver((entries) => {
    const ready = [];
    for (const { target: video } of entries) {
      if (!eligible(video)) {
        sizes.unobserve(video);
        watched.delete(video);
      } else if (video.getBoundingClientRect().width >= MIN_WIDTH) {
        ready.push(video);
      }
    }
    for (const video of ready) {
      sizes.unobserve(video);
      watched.delete(video);
      if (eligible(video)) addButton(video);
    }
  });

  const watch = (video) => {
    if (!eligible(video) || watched.has(video)) return;
    watched.add(video);
    sizes.observe(video);
  };

  // First pass on DOMContentLoaded measures directly (one layout, as before), so videos already in
  // the page get their button and reduced-motion pause straight away.
  const firstScan = () =>
    document.querySelectorAll('video[autoplay]').forEach((video) => {
      if (!eligible(video)) return;
      if (video.getBoundingClientRect().width >= MIN_WIDTH) addButton(video);
      else watch(video);
    });
  const scan = () => document.querySelectorAll('video[autoplay]').forEach(watch);

  let scanQueued = false;
  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    setTimeout(() => {
      scanQueued = false;
      scan();
    }, 100);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', firstScan);
  else firstScan();
  new MutationObserver(queueScan).observe(document.documentElement, { childList: true, subtree: true });
})();
